export const INDIAN_POKER_CARD_VALUES = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10,
] as const;

export type IndianPokerCardValue = (typeof INDIAN_POKER_CARD_VALUES)[number];

export const INDIAN_POKER_STARTING_CHIPS = 20;
export const INDIAN_POKER_ANTE = 1;
export const INDIAN_POKER_MIN_STARTING_CHIPS = INDIAN_POKER_ANTE + 1;
export const INDIAN_POKER_MIN_PLAYERS = 2;
export const INDIAN_POKER_MAX_PLAYERS = 2;

export type IndianPokerStatus = "IN_PROGRESS" | "COMPLETED";

export type IndianPokerRoundPhase = "BETTING" | "ROUND_RESULT" | "GAME_OVER";

export type IndianPokerRoundResultReason =
  | "HIGH_CARD"
  | "FOLD"
  | "TEN_FOLD_PENALTY"
  | "TIE";

export interface ValidationResult {
  readonly valid: boolean;
  readonly error?: string;
  readonly details?: Record<string, unknown>;
}

export interface InitGameOptions {
  readonly playerIds: readonly string[];
  readonly hostPlayerId: string;
  readonly seed: string;
  readonly gameId?: string;
  readonly startingChips?: number;
}

export interface IndianPokerRngState {
  readonly seed: string;
  readonly state: number;
}

export interface IndianPokerCard {
  readonly id: string;
  readonly value: IndianPokerCardValue;
}

export interface IndianPokerPlayer {
  readonly playerId: string;
  readonly chips: number;
  readonly currentBet: number;
}

export interface IndianPokerPriorityDraw {
  readonly playerId: string;
  readonly value: IndianPokerCardValue;
}

export interface IndianPokerRound {
  readonly roundNumber: number;
  readonly phase: IndianPokerRoundPhase;
  readonly activePlayerId: string;
  readonly bettingPriorityPlayerId: string;
  readonly pot: number;
  readonly carryoverPot: number;
  readonly actedPlayerIds: readonly string[];
  readonly cardsByPlayerId: Readonly<Record<string, IndianPokerCard>>;
  readonly winnerPlayerId?: string;
  readonly foldedPlayerId?: string;
  readonly resultReason?: IndianPokerRoundResultReason;
  readonly confirmedPlayerIds: readonly string[];
}

export interface IndianPokerState {
  readonly gameId: string;
  readonly status: IndianPokerStatus;
  readonly hostPlayerId: string;
  readonly players: readonly IndianPokerPlayer[];
  readonly round: IndianPokerRound;
  readonly completedRounds: readonly IndianPokerRound[];
  readonly deck: readonly IndianPokerCard[];
  readonly discardPile: readonly IndianPokerCard[];
  readonly deckGeneration: number;
  readonly rngState: IndianPokerRngState;
  readonly initialPriorityDraws: readonly IndianPokerPriorityDraw[];
  readonly winnerPlayerId?: string;
}

export interface IndianPokerCallAction {
  readonly type: "CALL";
  readonly playerId: string;
}

export interface IndianPokerRaiseAction {
  readonly type: "RAISE";
  readonly playerId: string;
  readonly totalBet: number;
}

export interface IndianPokerFoldAction {
  readonly type: "FOLD";
  readonly playerId: string;
}

export interface IndianPokerConfirmRoundResultAction {
  readonly type: "CONFIRM_ROUND_RESULT";
  readonly playerId: string;
}

export type IndianPokerAction =
  | IndianPokerCallAction
  | IndianPokerRaiseAction
  | IndianPokerFoldAction
  | IndianPokerConfirmRoundResultAction;

export interface IndianPokerViewerCard {
  readonly playerId: string;
  readonly card: IndianPokerCard | null;
  readonly hidden: boolean;
}

export interface IndianPokerViewerRound {
  readonly roundNumber: number;
  readonly phase: IndianPokerRoundPhase;
  readonly activePlayerId: string;
  readonly bettingPriorityPlayerId: string;
  readonly pot: number;
  readonly carryoverPot: number;
  readonly actedPlayerIds: readonly string[];
  readonly cards: readonly IndianPokerViewerCard[];
  readonly winnerPlayerId?: string;
  readonly foldedPlayerId?: string;
  readonly resultReason?: IndianPokerRoundResultReason;
  readonly confirmedPlayerIds: readonly string[];
}

export interface IndianPokerViewerState {
  readonly gameId: string;
  readonly status: IndianPokerStatus;
  readonly hostPlayerId: string;
  readonly players: readonly IndianPokerPlayer[];
  readonly round: IndianPokerViewerRound;
  readonly completedRounds: readonly IndianPokerRound[];
  readonly deckGeneration: number;
  readonly initialPriorityDraws: readonly IndianPokerPriorityDraw[];
  readonly winnerPlayerId?: string;
}
